import { Auth0Provider } from '@auth0/auth0-react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  AuthProvider,
  FlagsmithProvider,
  MintAdminServiceLocator,
} from '@mint-lib/admin-routing-context';
import i18n, { I18nextProvider, i18nInitPromise } from '@mint-lib/i18n';
import { Toaster } from '@mint-lib/notifications';
import { QueryClientProvider } from '@mint-lib/query-client';
import getTheme, { GlobalStyles, ThemeProvider } from '@mint-lib/theme';
import {
  routerContext,
  routerInstance,
  RouterProvider,
} from '@mint/admin-routing';
import { createRoot } from 'react-dom/client';

import './theme-support.css';

import { CssBaseline } from '@myn/mui/material';

import { muteConsole } from './utils/muteConsole.js';

type ThemeModeType = 'dark' | 'light';

const storedThemeValue = localStorage.getItem('theme') as ThemeModeType;
const isValidStoredThemeValue =
  storedThemeValue === 'light' || storedThemeValue === 'dark';

// watch for auth events
routerContext.serviceLocator.getEventBus().on('auth', 'auth', () => {
  if (routerInstance.state.matches[1].routeId.startsWith('/_auth')) {
    routerInstance.invalidate().finally(() => {
      if (routerInstance.latestLocation.pathname !== '/') {
        return;
      }
      routerInstance.navigate({
        to:
          'next' in routerInstance.latestLocation.search
            ? decodeURIComponent(
                (routerInstance.latestLocation.search.next as string) ?? '/',
              )
            : '/',
      });
    });
  }
});

routerContext.serviceLocator.getEventBus().on('auth', 'logout', async () => {
  await routerInstance.invalidate();
});

async function boot() {
  muteConsole();
  const credentials = await routerContext.getAuth0Credentials?.();
  const queryClient =
    await routerContext.serviceLocator.getOrThrowInstance('queryClient');
  const AppWrapper = () => {
    const [themeMode, setThemeMode] = useState<ThemeModeType>(
      isValidStoredThemeValue ? storedThemeValue : 'light',
    );
    useEffect(() => {
      document.documentElement.dataset.colorScheme = themeMode;
      if (themeMode === 'dark') {
        document.body.classList.add('dark');
      } else {
        document.body.classList.remove('dark');
      }
    }, [themeMode]);

    useEffect(() => {
      const channel = new BroadcastChannel('themeChannel');
      channel.onmessage = (event) => {
        const themeValue = event.data as ThemeModeType;
        if (themeValue === 'light' || themeValue === 'dark') {
          setThemeMode(event.data);
        }
      };
      return () => {
        channel.close();
      };
    }, []);

    const theme = useMemo(
      () => ({
        ...getTheme(themeMode),
      }),
      [themeMode],
    );

    return (
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <MintAdminServiceLocator.Provider
            value={routerContext.serviceLocator}
          >
            <FlagsmithProvider name="@mint/app">
              <QueryClientProvider client={queryClient}>
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <GlobalStyles />
                  <AuthProvider>
                    <Auth0Provider
                      domain={credentials?.domain ?? ''}
                      clientId={credentials?.applicationClientId ?? ''}
                      authorizationParams={{
                        redirect_uri:
                          globalThis.location.origin + '/login-auth0',
                      }}
                    >
                      <RouterProvider
                        router={routerInstance}
                        context={routerContext}
                      />
                      <Toaster />
                    </Auth0Provider>
                  </AuthProvider>
                </ThemeProvider>
              </QueryClientProvider>
            </FlagsmithProvider>
          </MintAdminServiceLocator.Provider>
        </I18nextProvider>
      </React.StrictMode>
    );
  };
  return i18nInitPromise.then(() =>
    createRoot(document.getElementById('root') as Element).render(
      <AppWrapper />,
    ),
  );
}
boot().catch((e) => {
  console.error(e);
});
